import { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Image as KonvaImage, Rect, Text, Group } from 'react-konva';
import Konva from 'konva';

import { loadDisplayImage } from '../../utils/e2eMedia';
import { getColorByClassId, CLASS_COLORS_MAP, getDetectionsToDraw } from '../../utils/skinResultsUtils';
import { normalizeConditionName } from '../../utils/skinConditionNormalization';

function useLoadedImage(src) {
  const [image, setImage] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) {
      setImage(null);
      setFailed(false);
      return undefined;
    }
    let cancelled = false;
    setFailed(false);
    loadDisplayImage(src)
      .then((img) => {
        if (!cancelled) setImage(img);
      })
      .catch(() => {
        if (!cancelled) {
          setImage(null);
          setFailed(true);
        }
      });
    return () => { cancelled = true; };
  }, [src]);

  return { image, failed };
}

const PulseRect = ({ box, selected }) => {
  const rectRef = useRef(null);

  useEffect(() => {
    if (!selected || !rectRef.current) return undefined;
    const node = rectRef.current;
    const anim = new Konva.Animation((frame) => {
      const pulse = 1 + Math.sin((frame.time / 280) * Math.PI * 2) * 0.12;
      node.strokeWidth(2.5 + pulse * 1.8);
      node.shadowBlur(8 + pulse * 10);
    }, node.getLayer());
    anim.start();
    return () => anim.stop();
  }, [selected, box.id]);

  return (
    <Rect
      ref={rectRef}
      x={box.x}
      y={box.y}
      width={box.width}
      height={box.height}
      stroke={box.color}
      strokeWidth={selected ? 3.5 : 2}
      dash={selected ? [] : [6, 4]}
      shadowColor={selected ? box.color : undefined}
      shadowBlur={selected ? 14 : 0}
      shadowOpacity={selected ? 0.5 : 0}
      cornerRadius={selected ? 4 : 2}
      onMouseEnter={() => box.onHover?.(box)}
      onMouseLeave={() => box.onHover?.(null)}
      onClick={() => box.onClick?.(box)}
    />
  );
};

const DetectionCanvas = ({
  imageUrl,
  detections = [],
  selectedConcern,
  containerWidth,
  containerHeight,
  fitSquare = false,
}) => {
  const { image, failed } = useLoadedImage(imageUrl);
  const [hoveredBox, setHoveredBox] = useState(null);

  const natural = useMemo(() => ({
    width: image?.naturalWidth || image?.width || 1,
    height: image?.naturalHeight || image?.height || 1,
  }), [image]);

  const layout = useMemo(() => {
    const maxW = containerWidth > 0 ? containerWidth : 1;
    const maxH = containerHeight > 0 ? containerHeight : maxW;
    const scale = fitSquare
      ? Math.min(maxW / natural.width, maxH / natural.height)
      : (maxW / natural.width);
    const stageWidth = Math.round(natural.width * scale);
    const stageHeight = Math.round(natural.height * scale);
    return { scale, stageWidth, stageHeight };
  }, [containerWidth, containerHeight, fitSquare, natural.height, natural.width]);

  const { scale, stageWidth, stageHeight } = layout;

  const filtered = useMemo(() => {
    if (!selectedConcern) return [];
    return detections.filter((d) => {
      const name = normalizeConditionName(d?.class_name || d?.className || '');
      return name === selectedConcern;
    });
  }, [detections, selectedConcern]);

  const boxes = useMemo(() => {
    if (!selectedConcern) return [];
    const primary = getDetectionsToDraw(selectedConcern, filtered, natural.width);
    return primary
      .map((d, idx) => {
        const bbox = d?.bbox || [];
        if (bbox.length !== 4) return null;
        const className = normalizeConditionName(d?.class_name || d?.className || 'Unknown');
        const color = CLASS_COLORS_MAP[className]
          || getColorByClassId(d?.class_id ?? d?.classId ?? 0);
        const confRaw = d?.confidence ?? d?.score ?? 0;
        const conf = confRaw <= 1 ? Math.round(confRaw * 100) : Math.round(confRaw);
        return {
          id: `${className}-${idx}`,
          className,
          x: bbox[0] * scale,
          y: bbox[1] * scale,
          width: (bbox[2] - bbox[0]) * scale,
          height: (bbox[3] - bbox[1]) * scale,
          color,
          confidence: conf,
          label: `${className} ${Number.isFinite(conf) ? `${conf}%` : ''}`.trim(),
          rawBbox: bbox,
          selected: Boolean(selectedConcern),
          onHover: setHoveredBox,
          onClick: () => {},
        };
      })
      .filter(Boolean);
  }, [filtered, natural.width, scale, selectedConcern]);

  if (!imageUrl) {
    return <div className="skin-routine-concern-preview-empty">No image available</div>;
  }

  if (failed) {
    return (
      <div className="skin-result-canvas-empty">
        <p>Image failed to load from storage.</p>
        {imageUrl.startsWith('data:') && (
          <img src={imageUrl} alt="" className="skin-result-canvas-fallback-img" />
        )}
      </div>
    );
  }

  return (
    <div className={`skin-result-stage-shell${fitSquare ? ' skin-result-stage-shell--fit' : ''}`}>
      <Stage
        width={stageWidth}
        height={stageHeight}
        className="skin-result-detection-stage"
      >
        <Layer>
          {image && (
            <KonvaImage
              image={image}
              width={stageWidth}
              height={stageHeight}
            />
          )}
          {boxes.map((box) => (
            <PulseRect key={box.id} box={box} selected={box.selected} />
          ))}
          {boxes.map((box) => (
            <Text
              key={`${box.id}-label`}
              x={box.x}
              y={Math.max(0, box.y - 18)}
              text={box.label}
              fontSize={11}
              fill="#fff"
              padding={4}
              fillAfterStrokeEnabled
              stroke="#111827"
              strokeWidth={0.4}
              listening={false}
            />
          ))}
          {hoveredBox && (
            <Group>
              <Rect
                x={hoveredBox.x}
                y={Math.max(0, hoveredBox.y - 34)}
                width={Math.max(90, hoveredBox.label.length * 6.2)}
                height={22}
                fill="rgba(15,23,42,0.88)"
                cornerRadius={6}
              />
              <Text
                x={hoveredBox.x + 6}
                y={Math.max(0, hoveredBox.y - 30)}
                text={`${hoveredBox.className} · ${hoveredBox.confidence}%`}
                fontSize={11}
                fill="#f8fafc"
                listening={false}
              />
            </Group>
          )}
        </Layer>
      </Stage>
    </div>
  );
};

export default DetectionCanvas;
